import Icon, { IconType } from 'components/Icon';
import { Loader } from 'components/Widget/Loader';
import Pill from 'components/Widget/Pill';
import { useMemo } from 'react';
import * as S from './styles';

export interface AccessOrg {
	id: string;
	name: string;
	granted: boolean;
}

interface AccessListProps {
	currentUserEmail?: string;
	emails: string[]; // active editors
	pendingEmails: string[]; // invited, no account yet
	onRemoveUser: (email: string) => void;
	removingEmail: string | null;
	// at most one team can be granted (constraint enforced server-side); ungranted teams
	// surface in the team picker, not here
	grantedOrg: AccessOrg | null;
	onRemoveOrg: (orgId: string) => void;
	removingOrgId: string | null;
}

const AccessList = ({
	currentUserEmail,
	emails,
	pendingEmails,
	onRemoveUser,
	removingEmail,
	grantedOrg,
	onRemoveOrg,
	removingOrgId,
}: AccessListProps) => {
	//----------------------------------------------------------------------
	// UTILITY
	//----------------------------------------------------------------------

	const sortedActive = useMemo(
		() =>
			emails
				.filter((e) => e !== currentUserEmail)
				.sort((a, b) => a.localeCompare(b)),
		[emails, currentUserEmail]
	);

	const sortedPending = useMemo(
		() => [...pendingEmails].sort((a, b) => a.localeCompare(b)),
		[pendingEmails]
	);

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const renderRowAction = (
		isInFlight: boolean,
		onClick: () => void,
		ariaLabel: string
	) => (
		<S.RowAction
			onClick={onClick}
			disabled={isInFlight}
			aria-label={ariaLabel}
			title="Remove"
		>
			{isInFlight ? (
				<Loader size={12} />
			) : (
				<Icon type={IconType.CLOSE} width={14} height={14} />
			)}
		</S.RowAction>
	);

	return (
		<S.Base>
			{currentUserEmail && (
				<S.Row>
					<S.RowLabel>
						<S.Email>{currentUserEmail}</S.Email>
						<S.YouTag>(you)</S.YouTag>
					</S.RowLabel>
				</S.Row>
			)}
			{sortedActive.map((email) => (
				<S.Row key={`active-${email}`}>
					<S.RowLabel>
						<S.Email>{email}</S.Email>
					</S.RowLabel>
					{renderRowAction(
						removingEmail === email,
						() => onRemoveUser(email),
						`Remove ${email}`
					)}
				</S.Row>
			))}
			{sortedPending.map((email) => (
				<S.Row key={`pending-${email}`}>
					<S.RowLabel>
						<S.Email>{email}</S.Email>
						<Pill label="Invited" variant="muted" />
					</S.RowLabel>
					{renderRowAction(
						removingEmail === email,
						() => onRemoveUser(email),
						`Remove ${email}`
					)}
				</S.Row>
			))}
			{grantedOrg && (
				<S.Row>
					<S.RowLabel>
						<S.Email>{grantedOrg.name}</S.Email>
						<Pill label="Team" variant="muted" />
					</S.RowLabel>
					{renderRowAction(
						removingOrgId === grantedOrg.id,
						() => onRemoveOrg(grantedOrg.id),
						`Remove team ${grantedOrg.name}`
					)}
				</S.Row>
			)}
		</S.Base>
	);
};

export default AccessList;
